/**
 * Trademark entity factory.
 *
 * A canonical Trademark always has the shape returned by
 * `createTrademark()` — regardless of whether the raw input came
 * from a mock fixture or a future REST/GraphQL backend.
 *
 * Stores and components depend on this shape, not on the raw
 * payload. That decoupling is what lets the backend swap be a
 * one-file change.
 */

import { riskFromScore } from '@/utils/risk'
import type { RawRecord, RiskLevel } from './raw'

export type TrademarkType = 'wordmark' | 'figurative' | 'combined'
export type MonitoringState = 'active' | 'paused' | 'none'

export interface Trademark {
  id: string | number | null
  name: string
  logo: string | null
  /**
   * Whether the mark has a logo worth fetching.
   *
   * Separate from `logo` because the bytes come from an authenticated endpoint
   * one request at a time. Without this flag a component cannot tell "no
   * picture" from "picture not fetched yet", so it either fires a blob request
   * per row — fifty of them to draw a table — or fires none and every real
   * logo stays invisible.
   */
  hasLogo: boolean
  initials: string | null
  regNumber: string
  regDate: string | null
  filingDate: string | null
  expiresOn: string | null
  jurisdiction: string
  classes: string[]
  type: TrademarkType | string
  owner: string
  clientId: string | number | null
  status: string
  monitoring: MonitoringState | string
  description: string
  /** Feed codes this mark is watched on (D-005) — EUIPO, WIPO, BG. */
  feeds: string[]
  /**
   * The scope rows themselves, each with its scan cursor (D-003).
   *
   * A named field rather than a dig into `raw`: `raw` holds whatever was passed
   * to the factory, which for a live row is the *adapted* object — so
   * `raw.scopes` was always undefined and the dossier showed "1 registry" above
   * "this mark is not being watched".
   */
  scopes: { feedId: number; feedCode: string; active: boolean; lastScanAt: string | null }[]
  /** Matches still awaiting review, and open legal procedures. */
  openMatches: number
  openCases: number
  /**
   * Risk has **no backend source for a brand** (W3.M7).
   *
   * `clients.risk_level` exists; `brands` has no equivalent column and no
   * decision defining one. The fixtures gave every mark a risk, which is why
   * the portfolio page could show "18 high-risk protected marks" — a number
   * with nothing behind it. Live rows leave this at 'low' and the UI shows
   * review workload (`openMatches`) instead, which is measured.
   */
  risk: RiskLevel
  riskScore: number | null
  viennaCodes: string[]
  /** Pre-selected in a list. Fixtures set it; the factory used to drop it. */
  selected: boolean
  /** Escape hatch for fields no component has needed to name yet. */
  raw: RawRecord
}

export function createTrademark(raw: RawRecord = {}): Trademark {
  const risk = raw.risk ?? riskFromScore(raw.riskScore)
  return {
    id: raw.id ?? null,
    name: raw.name ?? '',
    logo: raw.logo ?? null,
    hasLogo: raw.hasLogo ?? Boolean(raw.logo),
    initials: raw.initials ?? null,
    regNumber: raw.regNumber ?? '',
    regDate: raw.regDate ?? null,
    filingDate: raw.filingDate ?? null,
    expiresOn: raw.expiresOn ?? null,
    jurisdiction: raw.jurisdiction ?? '',
    classes: Array.isArray(raw.classes)
      ? raw.classes
      : raw.classes
        ? String(raw.classes)
            .split(',')
            .map((c) => c.trim())
        : [],
    type: raw.type ?? 'wordmark', // wordmark | figurative | combined
    owner: raw.owner ?? '',
    clientId: raw.clientId ?? null,
    status: raw.status ?? 'pending', // see TRADEMARK_STATUSES
    monitoring: raw.monitoring ?? 'active', // active | paused | none
    description: raw.description ?? '',
    feeds: Array.isArray(raw.feeds) ? raw.feeds : [],
    scopes: Array.isArray(raw.scopes) ? raw.scopes : [],
    openMatches: raw.openMatches ?? 0,
    openCases: raw.openCases ?? 0,
    risk,
    riskScore: raw.riskScore ?? null,
    viennaCodes: Array.isArray(raw.viennaCodes) ? raw.viennaCodes : [],
    selected: raw.selected ?? false,
    raw, // keep escape hatch for unmapped fields
  }
}

export function createTrademarkList(rawList: RawRecord[] = []): Trademark[] {
  return rawList.map(createTrademark)
}
